import { useEffect, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { useSubscription } from 'react-stomp-hooks';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { UseBaseTable } from 'hooks/useBaseTable';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { useVersionStore } from 'store/version.store';

import {
    pptxTableNarrativeHeaders,
    serializedOneLevelDataStructureTableToPPTX,
} from 'components/common/ExportDropdown/exportDropdown.utils';

import { riskConfig } from 'components/Dashboards/ExcellenceApps/riskConfig';
import { UseRowsBoardProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.provider';
import { isFiltersContainStatusChanged } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.utils';

interface Props {
    api: UseRowsBoardProvider['api'];
    ui: UseRowsBoardProvider['ui'];
    title: string;
    subTitle: string;
    table: UseBaseTable['table'];
    latestVersionId: number | undefined;
}

export const useRowsBoardLogic = ({ api, ui, table, title, subTitle, latestVersionId }: Props) => {
    const boundaryRef = useRef<HTMLDivElement>(null);
    const { ability, email } = useUserHook();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { handleSnackBar } = useCustomSnackBar();
    const [isDownloadCsv, setIsDownloadCsv] = useState<boolean>(false);
    const [isXlsExportModal, setIsXlsExportModal] = useState<boolean>(false);
    const currentVersion = useVersionStore((state) => state.version);
    const history = useHistory();

    const isStatusChangedFilterSelected = isFiltersContainStatusChanged(ui.state.status);
    const handleAPICsvExportPrivilege = ability.can('view', 'FileDownload');

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body);
        if (type === 'CsvFileReady') {
            setIsDownloadCsv(false);
            const { fileName } = message;
            if (fileName) {
                api.downloadCsvFile.instance.mutate({ fileName, downloadName: `${title}_${subTitle}` });
                sendEventWithDimensions({
                    category: 'Export',
                    action: 'CSV',
                    label: `${title} ${subTitle}`,
                });
            }
        }
    });

    const counter = api.listCardsData.instance.data?.numOfAllCards ?? 0;

    useEffect(() => {
        if (api.listCardsData.instance.data) {
            table.setTotalPages(api.listCardsData.instance.data.numOfPages);
        }
    }, [api.listCardsData.instance.data]);

    const pptxData = serializedOneLevelDataStructureTableToPPTX({
        data: api.listCardsData.instance.data?.cardList,
        sortBy: { key: 'endDate', sortOrder: 'asc' },
        headers: pptxTableNarrativeHeaders,
    });

    const exportIsLoading =
        isDownloadCsv || api.rowsBoardToXls.instance.isLoading || api.exportXerOrMpp.instance.isLoading;

    const handleAPICsvExport = ({ isCompact = false }: { isCompact?: boolean }) => {
        setIsDownloadCsv(true);
        api.rowsBoardToCsv.instance.mutate({
            ...api.rowsBoardToCsv.payload,
            fileName: `${title}_${subTitle}`,
            isCompact: isCompact,
        });
    };

    const handleAPIXlsExport = (data) => {
        api.rowsBoardToXls.instance.mutate(
            {
                ...api.rowsBoardToXls.payload,
                fileName: `${title}_${subTitle}`,
                configRequest: {
                    contractId: data.contractId,
                    isExtractRemainingDuration: data.remainingDurationInDays,
                    isExtractPercentageComplete: data.submittedDurationComplete,
                    userData: data.userData,
                },
            },
            {
                onSuccess: () => {
                    handleSnackBar('File Successfully Downloaded', 'success');

                    sendEventWithDimensions({
                        category: 'Export',
                        action: 'XLS',
                        label: `${title} ${subTitle}`,
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );
    };

    const handleXerOrMppExport = ({ exportType }: { exportType: 'XER' | 'MPP' }) => {
        if (latestVersionId) {
            api.exportXerOrMpp.instance.mutate(
                {
                    metaDataId: latestVersionId,
                    exportType: exportType,
                    fileName: `${title}_${subTitle}`,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');

                        sendEventWithDimensions({
                            category: 'Export',
                            action: exportType,
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    const runSimulation = () => {
        if (currentVersion) {
            history.push(
                AppRoutes.buildPath(
                    AppRoutes.DASHBOARD_PLANNING_PATH,
                    riskConfig.scheduleSimulation.link,
                    currentVersion.versionHashCode,
                ),
            );
        }
    };

    return {
        logic: {
            isStatusChangedFilterSelected,
            handleAPICsvExportPrivilege,
            boundaryRef,
            pptxData,
            setIsDownloadCsv,
            isDownloadCsv,
            exportIsLoading,
            handleAPICsvExport,
            handleAPIXlsExport,
            handleXerOrMppExport,
            runSimulation,
            isXlsExportModal,
            setIsXlsExportModal,
            counter,
        },
    };
};
